import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AppSharedModule } from 'app/shared/app-shared.module';
import { BaseComponent } from 'app/shared/components/base/base.component';
import { ProgramChangeMessage } from 'app/shared/models';
import { AppService } from 'app/shared/services';
import { ProgramInfo, ProgramPermission, ProgramsService, ProgramSummary } from 'app/shared/services/api-generated';
import { AuthService } from 'app/shared/services/auth/auth.service';
import { UserPreferencesService } from 'app/shared/services/app/user-preferences.service';
import { StorageService } from 'app/shared/services/storage/storage.service';
import { environment } from 'environments/environment';
import { ButtonModule } from 'primeng/button';
import { debounceTime, filter } from 'rxjs/operators';
import { SubscriptionNudgeDialog } from './dialogs/subscription-nudge-dialog/subscription-nudge.dialog';
import { Subject } from 'rxjs';



@Component({
  selector: 'miradi-subscription-nudge-banner',
  templateUrl: './subscription-nudge-banner.component.html',
  styleUrls: ['./subscription-nudge-banner.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    AppSharedModule,
    ButtonModule,
    SubscriptionNudgeDialog,
  ],
})
export class SubscriptionNudgeBannerComponent extends BaseComponent implements OnInit {

  @ViewChild(SubscriptionNudgeDialog) dialog: SubscriptionNudgeDialog;

  showProgramMessage: boolean = false;
  showUserMessage: boolean = false;
  currentProgram: ProgramInfo | null = null;
  private programPermission: ProgramPermission | null = null;
  private refreshSubject = new Subject<void>();
  showBanner: boolean = false;

  constructor(
    private appService: AppService,
    private cdr: ChangeDetectorRef,
    private programsService: ProgramsService,
    private router: Router,
    private storage: StorageService,
    private userPreferencesService: UserPreferencesService,
    authService: AuthService,
  ) {
    super(authService);
  }

  ngOnInit() {
    this.subscriptions.push(
      this.refreshSubject.pipe(
        debounceTime(1000)
      ).subscribe(() => {
        this.refresh();
      })
    );

    // Subscribe to user data loading
    this.subscriptions.push(
      this.authService.getSessionUser(true)
      .subscribe(user => {
        if (user) {
          this.refreshSubject.next();
        }
      })
    );

    // Subscribe to program context changes
    this.subscriptions.push(
      this.appService.listenToProgramChangeMessage()
      .subscribe((message: ProgramChangeMessage) => {
        this.currentProgram = message?.program || null;
        this.programPermission = this.authService.currentProgramPermission;

        if (this.currentProgram) {
          this.programsService.getProgramSummary(this.currentProgram.identifier)
          .subscribe((programSummary: ProgramSummary) => {
            Object.assign(this.currentProgram, programSummary);
            this.refreshSubject.next();
          })
        } else {
          this.refreshSubject.next();
        }
      })
    );

    // Route-based program context detection - clear program context when navigating outside program pages
    this.subscriptions.push(
      this.router.events
        .pipe(filter(event => event instanceof NavigationEnd))
        .subscribe((event: NavigationEnd) => {
          const isInProgramContext = this.isUrlInProgramContext(event.url);

          // If we navigated away from program context and currently have a program, clear it
          if (!isInProgramContext && this.currentProgram) {
            this.currentProgram = null;
            this.programPermission = null;
          }

          // Always refresh on navigation to honor suppression window and user changes
          this.refreshSubject.next();
        })
    );
  }

  private refresh() {
    const suppressed = this.isSuppressed();
    const dialogSuppressed = this.isDialogSuppressed();
    const inProgram = this.inProgramContext;

    let associatedProgramSubscription = null;
    if (inProgram) {
      associatedProgramSubscription = this.authService.currentUser?.associatedSubscriptions?.find((a: any) => a.programIdentifier && a.programIdentifier === this.currentProgram?.identifier);
    }
    const status = associatedProgramSubscription?.subscriptionStatus?.toLowerCase();
    const showProgram = inProgram && status && status !== 'active';
    const showUser = !!this.authService.currentUser?.shouldNudgeForSubscription;

    this.showProgramMessage = showProgram;
    this.showUserMessage = showUser;
    this.showBanner = environment.featureFlags.subscriptionNudgeBanner
    && !suppressed
    && (showProgram || showUser);

    // Show dialog when banner should be displayed and dialog is not suppressed
    if (this.showBanner && showUser && !dialogSuppressed && !this.dialog?.isVisible) {
      this.showDialog();
    }

    this.cdr.markForCheck();
  }

  private isSuppressed(): boolean {
    const until = Number(this.storage.getItem('miradi_subscription_nudge_suppressed_until'));
    if (!until) return false;
    if (Date.now() < until) return true;
    this.storage.removeItem('miradi_subscription_nudge_suppressed_until');
    return false;
  }

  private isDialogSuppressed(): boolean {
    const lastDismissedDate = this.authService.currentUserPreferences?.value?.nudgeBannerLastDismissedDateTime;
    if (!lastDismissedDate) return false;

    const oneWeekInMs = 7 * 24 * 60 * 60 * 1000;
    const lastDismissedTime = new Date(lastDismissedDate).getTime();
    const now = Date.now();

    return (now - lastDismissedTime) < oneWeekInMs;
  }

  get inProgramContext(): boolean {
    return !!this.currentProgram;
  }

  /**
   * Returns the mailto link for program contact.
   * Falls back to generic support email if program contact email is not available.
   */
  get programContactMailto(): string {
    const programEmail = this.currentProgram?.contactEmail;
    if (programEmail && programEmail.trim()) {
      return `mailto:${programEmail}`;
    }
    // Generic support email fallback
    return 'mailto:support@miradi.org';
  }

  /**
   * Determines if the given URL represents a program or project context page.
   * Program context includes:
   * - /program/... routes
   * - /project/... routes (projects belong to programs)
   * - /ux/program/... routes
   * - /ux/project/... routes
   */
  private isUrlInProgramContext(url: string): boolean {
    return url.includes('/program/') || url.includes('/project/');
  }

  viewSubscriptionOptions() {
    // Navigate to the account page
    this.router.navigate(['/account']);
  }

  showDialog() {
    this.dialog?.show();
  }

}
